'use client';

import { useEffect } from 'react';
import { Link } from '@/components/LocaleLink';
import { Home, RotateCcw, AlertTriangle } from 'lucide-react';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);

    // ChunkLoadError = kunden har en flik öppen från ett tidigare bygge och den
    // content-hashade chunken finns inte längre (eller hann inte nå Cloudflares edge).
    // Då är felsidan fel svar — en enkel omladdning hämtar det nya bygget och kunden
    // kan fortsätta handla. Guard i sessionStorage så vi aldrig loopar om filen
    // verkligen är borta för gott.
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
        error?.message || ''
      );
    if (!isChunkError) return;
    try {
      if (sessionStorage.getItem('af:chunk-reload') === '1') return;
      sessionStorage.setItem('af:chunk-reload', '1');
      window.location.reload();
    } catch {
      // sessionStorage blockerad (privat läge) — hoppa hellre över än att riskera en loop
    }
  }, [error]);

  return (
    <div className="container mx-auto px-4 py-16 max-w-2xl text-center">
      <div className="mb-8">
        <AlertTriangle className="h-16 w-16 text-destructive/40 mx-auto" />
      </div>

      <h1 className="font-heading text-3xl font-bold mb-3">Något gick fel</h1>
      <p className="text-muted-foreground mb-8 text-lg">
        Ett oväntat fel uppstod. Försök igen eller gå tillbaka till startsidan.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={reset}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
        >
          <RotateCcw className="h-4 w-4" />
          Försök igen
        </button>
        <Link
          href="/"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 border rounded-lg font-medium hover:bg-muted transition-colors"
        >
          <Home className="h-4 w-4" />
          Till startsidan
        </Link>
      </div>

      <p className="text-sm text-muted-foreground mt-8">
        Kvarstår problemet? Kontakta oss på{' '}
        <a href="tel:042201620" className="text-primary hover:underline font-medium">042-20 16 20</a>
        {' '}eller{' '}
        <a href="mailto:info@autofrance.se" className="text-primary hover:underline font-medium">info@autofrance.se</a>
        {error?.digest && <span className="block mt-1 text-xs text-muted-foreground/60">Felkod: {error.digest}</span>}
      </p>
    </div>
  );
}
